import { useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { getStudioHref } from '@/lib/hostRouting'
import { hasAuthPresence } from '@/lib/authPresence'
import { prefetchDemoPage } from '@/lib/prefetchDemo'

// Signed-in state is detected client-side only (the presence cookie is
// unreadable during SSR/prerender), so the first render always matches the
// server markup and auth-aware CTAs flip after hydration.
export function usePublicAuthed() {
  const [authed, setAuthed] = useState(false)
  useEffect(() => setAuthed(hasAuthPresence()), [])
  return authed
}

/**
 * The one primary action on every public page: logged-out browsers get the
 * demo, signed-in browsers jump straight to their calculator on the studio
 * host. `compact` shortens the labels for tight spots like the site header.
 */
export function PublicPrimaryCta({ size, variant, className, compact = false, withArrow = false }) {
  const authed = usePublicAuthed()
  const arrow = withArrow ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /> : null

  return (
    <Button asChild size={size} variant={variant} className={className}>
      {authed ? (
        <a href={getStudioHref('/')}>
          {compact ? 'Open calculator' : 'Open the calculator'}
          {arrow}
        </a>
      ) : (
        <Link to="/demo" onPointerEnter={prefetchDemoPage} onFocus={prefetchDemoPage}>
          {compact ? 'Try demo' : 'Try the demo'}
          {arrow}
        </Link>
      )}
    </Button>
  )
}

/** Renders children only for logged-out browsers – e.g. "Log in" links. */
export function AuthHide({ children }) {
  const authed = usePublicAuthed()
  return authed ? null : children
}
